import { logger } from "@/utils/logger";
/**
 * Extract YouTube video ID from a URL
 */
export const extractYouTubeId = (url: string): string | null => {
  if (!url) return null;
  
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\?\/\s]+)/i,
    /youtube\.com\/watch\?.*v=([^&\?\/\s]+)/i
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }
  
  return null;
};

/**
 * Remove YouTube video URLs from text that match the given video IDs
 */
export const removeYouTubeReferencesFromText = (text: string, videoIds: Set<string>): string => {
  if (!text || videoIds.size === 0) return text;
  
  let cleanedText = text;
  
  // Remove full YouTube URLs that match our video IDs
  videoIds.forEach(videoId => {
    const patterns = [
      new RegExp(`https?://(?:www\\.)?youtube\\.com/watch\\?[^\\s]*v=${videoId}[^\\s]*`, 'gi'),
      new RegExp(`https?://(?:www\\.)?youtu\\.be/${videoId}[^\\s]*`, 'gi'),
      new RegExp(`https?://(?:www\\.)?youtube\\.com/embed/${videoId}[^\\s]*`, 'gi')
    ];
    
    patterns.forEach(pattern => {
      cleanedText = cleanedText.replace(pattern, '');
    });
  });
  
  // Clean up any leftover artifacts
  cleanedText = cleanedText
    .replace(/\s{2,}/g, ' ') // Multiple spaces
    .replace(/\n{3,}/g, '\n\n') // Multiple newlines
    .trim();
  
  return cleanedText;
};

/**
 * Deduplicate YouTube videos between Intro and Suggested_Models
 * Removes video references from Intro if they appear in Suggested_Models
 */
export const deduplicateYouTubeVideos = (aiResponse: any): any => {
  try {
    if (!aiResponse?.Reply_Message) return aiResponse;
    
    const intro = aiResponse.Reply_Message.Intro;
    const suggestedModels = aiResponse.Reply_Message.Content?.Showroom?.Suggested_Models;
    
    if (!intro || !suggestedModels || !Array.isArray(suggestedModels)) {
      return aiResponse;
    }
    
    // Collect all video IDs from Suggested_Models
    const videoIdsInModels = new Set<string>();
    
    suggestedModels.forEach((model: any) => {
      const videos = model.Youtube_Video || model.Youtube_Videos;
      if (Array.isArray(videos)) {
        videos.forEach((video: any) => {
          // Handle new format (youtube_id) or old format (Link/url)
          const videoId = video.youtube_id || extractYouTubeId(video.Link || video.url);
          if (videoId) {
            videoIdsInModels.add(videoId);
          }
        });
      }
    });
    
    if (videoIdsInModels.size === 0) {
      return aiResponse;
    }
    
    // Remove those video URLs from Intro
    const cleanedIntro = removeYouTubeReferencesFromText(intro, videoIdsInModels);
    
    if (cleanedIntro !== intro) {
      logger.log(`🎥 Removed ${videoIdsInModels.size} duplicate YouTube reference(s) from Intro`);
      
      return {
        ...aiResponse,
        Reply_Message: {
          ...aiResponse.Reply_Message,
          Intro: cleanedIntro
        }
      };
    }
    
    return aiResponse;
  } catch (error) {
    console.error('Error deduplicating YouTube videos:', error);
    return aiResponse;
  }
};
